// Grok-3 AI Message Parsing Service
// Uses xAI's Grok-3 API to parse incoming WhatsApp messages and extract load details
// Falls back to rule-based parsing when API not configured

const GROK_CONFIG_KEY = 'hmgc_grok_config';

export const getGrokConfig = () => {
  try {
    const config = localStorage.getItem(GROK_CONFIG_KEY);
    return config ? JSON.parse(config) : null;
  } catch (e) {
    console.error('Error reading Grok config:', e);
    return null;
  }
};

export const saveGrokConfig = (config) => {
  try {
    localStorage.setItem(GROK_CONFIG_KEY, JSON.stringify(config));
    return true;
  } catch (e) {
    console.error('Error saving Grok config:', e);
    return false;
  }
};

export const isGrokConfigured = () => {
  const config = getGrokConfig();
  return config && config.apiKey;
};

// Parse message using Grok-3 API
export const parseMessageWithGrok = async (message, customerInfo = null) => {
  const config = getGrokConfig();
  
  if (!isGrokConfigured()) {
    // Fall back to rule-based parsing
    const parsed = parseMessageRuleBased(message, customerInfo);
    return {
      ...parsed,
      method: 'rule-based',
      aiNote: 'Grok-3 not configured - using rule-based parsing. Add xAI API key in Settings for smarter extraction.'
    };
  }
  
  try {
    const response = await fetch('https://api.x.ai/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${config.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: config.model || 'grok-3',
        messages: [
          {
            role: 'system',
            content: `You are a trucking dispatch assistant. Parse incoming messages from customers to extract load details. Extract: pickup address, delivery address, commodity type, weight (in kg), dimensions (length, width, height in meters), pickup time, delivery time. If any field is missing or unclear, mark it as null and set a low confidence. Respond with JSON only.

Format:
{
  "pickupAddress": "string or null",
  "deliveryAddress": "string or null", 
  "commodity": "string or null",
  "weightKg": number or null,
  "dimensions": { "lengthM": number, "widthM": number, "heightM": number } or null,
  "pickupTime": "ISO datetime string or null",
  "deliveryTime": "ISO datetime string or null",
  "urgency": "normal" | "urgent" | "asap",
  "confidence": 0-100,
  "missingFields": ["list of fields that couldn't be extracted"],
  "guessedFields": ["list of fields that were inferred/guessed"],
  "customerIntent": "load_request" | "question" | "confirmation" | "delay_notice" | "other",
  "notes": "any additional context or warnings"
}`
          },
          {
            role: 'user',
            content: `Customer: ${customerInfo?.name || 'Unknown'}\nPhone: ${message.from || 'Unknown'}\n\nMessage:\n${message.body || message}`
          }
        ],
        temperature: 0.2,
        max_tokens: 1000
      })
    });
    
    if (!response.ok) {
      throw new Error(`Grok API error: ${response.status}`);
    }
    
    const result = await response.json();
    const aiResponse = result.choices[0]?.message?.content;
    
    // Parse the JSON response
    const parsed = parseGrokResponse(aiResponse);
    
    return {
      ...parsed,
      method: 'grok-3',
      rawResponse: aiResponse
    };
  } catch (error) {
    console.error('Grok API error:', error);
    // Fall back to rule-based parsing
    const parsed = parseMessageRuleBased(message, customerInfo);
    return {
      ...parsed,
      method: 'rule-based',
      aiError: error.message,
      aiNote: 'Grok-3 request failed - using rule-based parsing as fallback.'
    };
  }
};

// Parse Grok response JSON
const parseGrokResponse = (response) => {
  try {
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('No JSON found in response');
    }
    return JSON.parse(jsonMatch[0]);
  } catch (e) {
    console.error('Error parsing Grok response:', e);
    return {
      error: 'Failed to parse AI response',
      confidence: 0,
      missingFields: ['all'],
      customerIntent: 'other'
    };
  }
};

// Rule-based message parsing (fallback when Grok not available)
export const parseMessageRuleBased = (message, customerInfo = null) => {
  const text = typeof message === 'string' ? message : message.body || '';
  const lowerText = text.toLowerCase();
  
  const result = {
    pickupAddress: null,
    deliveryAddress: null,
    commodity: null,
    weightKg: null,
    dimensions: null,
    pickupTime: null,
    deliveryTime: null,
    urgency: 'normal',
    confidence: 0,
    missingFields: [],
    guessedFields: [],
    customerIntent: 'other',
    notes: ''
  };
  
  // Detect customer intent
  if (lowerText.includes('give me a sec') || lowerText.includes('hold on') || lowerText.includes('one moment')) {
    result.customerIntent = 'delay_notice';
    result.notes = 'Customer indicated they need time - start 10-minute timer';
    result.confidence = 90;
    return result;
  }
  
  if (lowerText.includes('?') || lowerText.includes('how much') || lowerText.includes('can you')) {
    result.customerIntent = 'question';
    result.confidence = 70;
  }
  
  if (lowerText.includes('confirm') || lowerText.includes('yes') || lowerText.includes('correct')) {
    result.customerIntent = 'confirmation';
    result.confidence = 80;
  }
  
  // Check for load request indicators
  const loadIndicators = ['pickup', 'pick up', 'deliver', 'delivery', 'load', 'haul', 'transport', 'move', 'need a truck'];
  if (loadIndicators.some(ind => lowerText.includes(ind))) {
    result.customerIntent = 'load_request';
  }
  
  // Extract addresses using common patterns
  const addressPatterns = [
    /(?:pickup|pick up|from|at)\s+(?:at\s+)?([^,.]+(?:,\s*[^,.]+){1,3})/gi,
    /(\d+\s+[A-Za-z\s]+(?:St|Street|Ave|Avenue|Rd|Road|Blvd|Boulevard|Dr|Drive|Ln|Lane|Way|Ct|Court)[^,]*(?:,\s*[A-Za-z\s]+)?(?:,?\s*[A-Z]{2})?)/gi
  ];
  
  const deliveryPatterns = [
    /(?:deliver|delivery|to|drop)\s+(?:to\s+|at\s+)?([^,.]+(?:,\s*[^,.]+){1,3})/gi,
    /(?:destination|going to)\s+([^,.]+(?:,\s*[^,.]+){1,3})/gi
  ];
  
  // Try to extract pickup address
  for (const pattern of addressPatterns) {
    const match = text.match(pattern);
    if (match && match[0]) {
      result.pickupAddress = cleanAddress(match[0]);
      result.guessedFields.push('pickupAddress');
      break;
    }
  }
  
  // Try to extract delivery address
  for (const pattern of deliveryPatterns) {
    const match = text.match(pattern);
    if (match && match[0]) {
      result.deliveryAddress = cleanAddress(match[0]);
      result.guessedFields.push('deliveryAddress');
      break;
    }
  }
  
  // Extract weight
  const weightPatterns = [
    /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:kg|kilograms?)/i,
    /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:lbs?|pounds?)/i,
    /(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:tons?|tonnes?)/i
  ];
  
  for (const pattern of weightPatterns) {
    const match = text.match(pattern);
    if (match) {
      let weight = parseFloat(match[1].replace(/,/g, ''));
      // Convert to kg
      if (pattern.source.includes('lbs')) {
        weight = weight * 0.453592;
      } else if (pattern.source.includes('ton')) {
        weight = weight * 1000;
      }
      result.weightKg = Math.round(weight);
      result.guessedFields.push('weightKg');
      break;
    }
  }
  
  // Extract dimensions
  const dimPattern = /(\d+(?:\.\d+)?)\s*(?:x|by)\s*(\d+(?:\.\d+)?)\s*(?:x|by)\s*(\d+(?:\.\d+)?)\s*(?:ft|feet|m|meters?)?/i;
  const dimMatch = text.match(dimPattern);
  if (dimMatch) {
    let l = parseFloat(dimMatch[1]);
    let w = parseFloat(dimMatch[2]);
    let h = parseFloat(dimMatch[3]);
    // Assume feet if not specified, convert to meters
    if (text.toLowerCase().includes('ft') || text.toLowerCase().includes('feet')) {
      l = l * 0.3048;
      w = w * 0.3048;
      h = h * 0.3048;
    }
    result.dimensions = { lengthM: l, widthM: w, heightM: h };
    result.guessedFields.push('dimensions');
  }
  
  // Extract commodity
  const commodities = [
    'steel', 'lumber', 'wood', 'concrete', 'gravel', 'sand', 'equipment', 
    'machinery', 'pipes', 'beams', 'blocks', 'materials', 'supplies',
    'pallets', 'containers', 'freight'
  ];
  for (const commodity of commodities) {
    if (lowerText.includes(commodity)) {
      result.commodity = commodity.charAt(0).toUpperCase() + commodity.slice(1);
      result.guessedFields.push('commodity');
      break;
    }
  }
  
  // Check urgency
  if (lowerText.includes('urgent') || lowerText.includes('asap') || lowerText.includes('immediately')) {
    result.urgency = 'urgent';
  } else if (lowerText.includes('rush') || lowerText.includes('today')) {
    result.urgency = 'asap';
  }
  
  // Extract time references
  const timePatterns = [
    /(?:at|by|before)\s+(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)/gi,
    /(?:tomorrow|today|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/gi
  ];
  
  for (const pattern of timePatterns) {
    const match = text.match(pattern);
    if (match) {
      result.notes += `Time reference found: ${match[0]}. `;
      result.guessedFields.push('time');
    }
  }
  
  // Calculate missing fields
  if (!result.pickupAddress) result.missingFields.push('pickupAddress');
  if (!result.deliveryAddress) result.missingFields.push('deliveryAddress');
  if (!result.commodity) result.missingFields.push('commodity');
  if (!result.weightKg) result.missingFields.push('weightKg');
  if (!result.dimensions) result.missingFields.push('dimensions');
  
  // Calculate confidence based on extracted fields
  const totalFields = 5; // pickup, delivery, commodity, weight, dimensions
  const extractedFields = totalFields - result.missingFields.length;
  result.confidence = Math.round((extractedFields / totalFields) * 100);
  
  // Reduce confidence for guessed fields
  result.confidence = Math.max(0, result.confidence - (result.guessedFields.length * 5));
  
  return result;
};

// Clean up extracted address
const cleanAddress = (address) => {
  return address
    .replace(/^(?:pickup|pick up|from|at|deliver|delivery|to|drop|destination|going to)\s*/i, '')
    .replace(/^\s*(?:at|to)\s*/i, '')
    .trim();
};

// Create draft load from parsed message
export const createDraftLoadFromMessage = (parsedMessage, customer = null) => {
  const now = new Date();
  
  return {
    load_number: `DRAFT-${Date.now()}`,
    status: 'pending',
    customer_id: customer?.id || null,
    commodity: parsedMessage.commodity || null,
    weight_kg: parsedMessage.weightKg || null,
    length_m: parsedMessage.dimensions?.lengthM || null,
    width_m: parsedMessage.dimensions?.widthM || null,
    height_m: parsedMessage.dimensions?.heightM || null,
    notes: parsedMessage.notes || '',
    created_at: now.toISOString(),
    is_draft: true,
    ai_confidence: parsedMessage.confidence,
    ai_method: parsedMessage.method,
    missing_fields: parsedMessage.missingFields,
    guessed_fields: parsedMessage.guessedFields,
    source_message: parsedMessage.rawMessage,
    // Stops will be created separately
    pickup_address: parsedMessage.pickupAddress,
    delivery_address: parsedMessage.deliveryAddress
  };
};

export default {
  getGrokConfig,
  saveGrokConfig,
  isGrokConfigured,
  parseMessageWithGrok,
  parseMessageRuleBased,
  createDraftLoadFromMessage
};
